/**
 * Typed API client. One axios instance + a typed fetcher per endpoint of the
 * FastAPI backend mounted under `<url_root>/api`.
 */
import axios from "axios";

import { config } from "@/config";
import { UNAUTHORIZED_EVENT, clearToken, getToken } from "@/lib/authToken";
import { thresholds } from "@/lib/thresholds";

export const http = axios.create({
  baseURL: config.apiBase,
  timeout: 15_000,
  headers: { Accept: "application/json" },
  withCredentials: true, // send the httpOnly auth cookie for same-origin requests
});

// Attach the bearer token (works cross-origin in dev where cookies won't flow).
http.interceptors.request.use((cfg) => {
  const token = getToken();
  if (token) {
    cfg.headers.set("Authorization", `Bearer ${token}`);
  }
  return cfg;
});

// On 401, drop the stale token and let the auth layer switch to the login view.
http.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401) {
      clearToken();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
      }
    }
    return Promise.reject(error);
  },
);

/* ------------------------------------------------------------------ */
/* Response types                                                      */
/* ------------------------------------------------------------------ */

export interface Health {
  status: string;
  db: boolean;
}

/** Home headline NRW. Meters reporting (`SensorHealthSummary`) and daily
 * consumption (`ConsumptionSummary`) are owned by their own payloads. */
export interface Kpis {
  /** Rolling-window NRW (`core.nrw_basis.headline`), never a single day. */
  nrw_percent: number;
  nrw_grade: string;
  /** Inlet days the NRW figure covers, and the last of them (ISO date). */
  nrw_days: number;
  nrw_as_of: string;
  as_of: string;
}

export interface ConsumptionDailyPoint {
  day: string;
  consumption_m3: number;
}

export interface NrwDailyPoint {
  day: string;
  siv_m3: number;
  billed_m3: number;
  nrw_percent: number;
}

export interface ForecastPoint {
  day: string;
  forecast_m3: number;
}

/* ------------------------------------------------------------------ */
/* Fetchers                                                            */
/* ------------------------------------------------------------------ */

export async function fetchHealth(signal?: AbortSignal): Promise<Health> {
  const { data } = await http.get<Health>("/health", { signal });
  return data;
}

/** Include `locality` in the query only when a real locality is selected. */
const loc = (locality?: string | null) => (locality ? { locality } : {});

export async function fetchKpis(
  locality?: string | null,
  signal?: AbortSignal,
): Promise<Kpis> {
  const { data } = await http.get<Kpis>("/kpis", { params: loc(locality), signal });
  return data;
}

export async function fetchConsumptionDaily(
  days = thresholds().default_analysis_days,
  locality?: string | null,
  signal?: AbortSignal,
): Promise<ConsumptionDailyPoint[]> {
  const { data } = await http.get<ConsumptionDailyPoint[]>("/consumption/daily", {
    params: { days, ...loc(locality) },
    signal,
  });
  return data;
}

export async function fetchNrwDaily(
  days = thresholds().default_analysis_days,
  signal?: AbortSignal,
): Promise<NrwDailyPoint[]> {
  const { data } = await http.get<NrwDailyPoint[]>("/nrw/daily", {
    params: { days },
    signal,
  });
  return data;
}

export async function fetchForecast(
  horizon = thresholds().forecast_days,
  locality?: string | null,
  signal?: AbortSignal,
): Promise<ForecastPoint[]> {
  const { data } = await http.get<ForecastPoint[]>("/forecast", {
    params: { horizon, ...(locality ? { locality } : {}) },
    signal,
  });
  return data;
}
